import { ApolloError, gql, useMutation } from '@apollo/client';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { descriptionForVariant } from '../../checksConfigPage/ChecksConfigPage';
import { VariantCheckbox } from '../../checksConfigPage/variantCheckbox/VariantCheckbox';
import { SharedCheckConfigCardProps } from '../common/SharedCheckConfigCardProps';
import { VariantCheckConfigFragment } from '../VariantCheckConfigFragment';

const variantCheckConfigDownstreamVariantsMutation = gql`
  mutation VariantCheckConfigDownstreamVariantsMutation(
    $graphId: ID!
    $graphVariant: String!
    $blockingDownstreamVariants: [String!]
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        updateCheckConfigurationDownstreamVariants(
          blockingDownstreamVariants: $blockingDownstreamVariants
        ) {
          id
          ...VariantCheckConfigFragment
        }
      }
    }
  }
  ${VariantCheckConfigFragment}
`;

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  blockingDownstreamVariants: Yup.array(Yup.string()).nullable(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

type Props = SharedCheckConfigCardProps & {
  downstreamVariants: string[];
  statsLoading: boolean;
  statsData: Map<
    string,
    GraphQLTypes.ChecksConfigPageStatsQuery_service_operationCheckRequestsByVariant
  >;
};

export const BlockingDownstreamVariantsCard = ({
  graphRef,
  variantCheckConfig,
  downstreamVariants,
  roles,
  statsLoading,
  statsData,
}: Props) => {
  const [updateConfigError, setUpdateConfigError] =
    useState<ApolloError | null>(null);

  const [updateCheckConfig] = useMutation<
    GraphQLTypes.VariantCheckConfigDownstreamVariantsMutation,
    GraphQLTypes.VariantCheckConfigDownstreamVariantsMutationVariables
  >(variantCheckConfigDownstreamVariantsMutation, {
    onError: setUpdateConfigError,
  });

  const { values, handleChange, submitForm } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      blockingDownstreamVariants:
        variantCheckConfig.downstreamVariantsConfig.blockingDownstreamVariants,
    },
    onSubmit: async (formValues) => {
      await updateCheckConfig({
        variables: {
          graphId: graphRef.graphId,
          graphVariant: graphRef.graphVariant,
          blockingDownstreamVariants: formValues.blockingDownstreamVariants,
        },
      });
    },
  });

  const clearErrorsAndSubmit = useCallback(
    (e?: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setUpdateConfigError(null);
      if (e) {
        handleChange(e);
      }
      submitForm();
    },
    [setUpdateConfigError, handleChange, submitForm],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Blocking downstream variants</CardTitle>
        <CardDescription>
          Select which downstream variants should block checks for{' '}
          <b>{graphRef.graphVariant}</b> if they fail to build or have breaking
          changes.
        </CardDescription>
      </CardHeader>
      <CardBody>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gridAutoRows: '1fr',
            gridColumnGap: '1rem',
            gridRowGap: '1rem',
            alignContent: 'start',
          }}
          className="text-base"
        >
          {downstreamVariants.map((variantName) => (
            <VariantCheckbox
              key={variantName}
              name="blockingDownstreamVariants"
              isChecked={values.blockingDownstreamVariants?.includes(
                variantName,
              )}
              isDisabled={!roles.canWriteCheckConfiguration}
              value={variantName}
              title={`${graphRef.graphId}@${variantName}`}
              description={descriptionForVariant(
                variantName,
                statsLoading,
                statsData,
              )}
              canWriteCheckConfiguration={roles.canWriteCheckConfiguration}
              onChange={clearErrorsAndSubmit}
            />
          ))}
        </div>

        {/* Form update error message */}
        {updateConfigError && <ErrorMessage error={updateConfigError} />}
      </CardBody>
    </Card>
  );
};
